/**
 * Collaboration Listener ID Utilities
 *
 * Centralized ID generation for all real-time collaboration listeners.
 * Prevents inconsistencies and typos across the codebase.
 */

import { debugLog, debugWarn } from '../../utils/debug';

export type ListenerType = 'milestoneMap' | 'objectives' | 'collaborators' | 'humans' | 'humanRelationships' | 'descendants';

/**
 * Generate a consistent listener ID for collaboration features
 *
 * @param type - The type of listener (milestoneMap, objectives, presence, etc.)
 * @param identifier - The UUID or identifier for the resource
 * @returns A consistently formatted listener ID
 *
 * @example
 * ```typescript
 * getListenerId('milestoneMap', 'uuid-123') // → 'milestoneMap_uuid-123'
 * getListenerId('objectives', 'uuid-123')   // → 'objectives_uuid-123'
 * getListenerId('presence', 'uuid-123')     // → 'presence_uuid-123'
 * ```
 */
export function getListenerId(type: ListenerType, identifier: string): string {
  if (!identifier || identifier.trim() === '') {
    throw new Error(`Invalid identifier provided for ${type} listener: "${identifier}"`);
  }

  return `${type}_${identifier}`;
}

/**
 * Parse a listener ID back into its components
 * Useful for debugging and cleanup operations
 *
 * @param listenerId - The listener ID to parse
 * @returns Object with type and identifier, or null if invalid format
 *
 * @example
 * ```typescript
 * parseListenerId('milestoneMap_uuid-123') // → { type: 'milestoneMap', identifier: 'uuid-123' }
 * parseListenerId('invalid-format')        // → null
 * ```
 */
export function parseListenerId(
  listenerId: string,
): { type: ListenerType; identifier: string } | null {
  const parts = listenerId.split('_');
  if (parts.length !== 2) {
    debugWarn(
      `⚠️ Invalid listener ID format: ${listenerId}, expected format: 'type_identifier'`,
    );
    return null;
  }

  const [type, identifier] = parts;

  // Validate type is a known ListenerType
  const validTypes: ListenerType[] = ['milestoneMap', 'objectives', 'collaborators', 'humans', 'humanRelationships', 'descendants'];
  if (!validTypes.includes(type as ListenerType)) {
    debugWarn(`⚠️ Invalid listener type: ${type}, expected one of: ${validTypes.join(', ')}`);
    return null;
  }

  return { type: type as ListenerType, identifier };
}

/**
 * Development helper - log listener ID information
 * Only available in development mode
 */
export function debugListenerId(listenerId: string): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const parsed = parseListenerId(listenerId);
  if (parsed) {
    debugLog(`🆔 Listener ID: ${listenerId}`, {
      type: parsed.type,
      identifier: parsed.identifier,
      valid: true,
    });
  } else {
    debugWarn(`⚠️ Invalid listener ID format: ${listenerId}`);
  }
}
